import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Building2, Calendar, ExternalLink, Facebook, Globe, Instagram, Linkedin, Mail, MapPin, MessageSquare, Phone, UserRound, X } from "lucide-react";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import ContactForm from "@/components/ContactForm";
import { useSeo } from "@/lib/seo";
import { publicUrl } from "@/lib/publicUrl";

const mediaUrl = (value) => !value || value.startsWith("http") || value.startsWith("data:")
    ? value
    : `${process.env.REACT_APP_BACKEND_URL}${value}`;
const pageContainerClass = "w-full max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20";

const SocialLinks = ({ social }) => {
    const links = [
        ["instagram", "Instagram", Instagram],
        ["facebook", "Facebook", Facebook],
        ["linkedin", "LinkedIn", Linkedin],
    ].filter(([key]) => social?.[key]);
    if (!links.length) return null;
    return (
        <div data-testid="agency-socials" className="mt-5 flex items-center gap-2">
            {links.map(([key, label, Icon]) => (
                <a key={key} data-testid={`agency-social-${key}`} href={social[key]} target="_blank" rel="noreferrer" aria-label={label} className="w-9 h-9 grid place-items-center rounded-full border border-white/15 text-ink-300 hover:text-gold hover:border-gold/40 transition-colors">
                    <Icon size={15} />
                </a>
            ))}
        </div>
    );
};

const ActionButtons = ({ agency, onContact, compact = false }) => (
    <div className={`flex ${compact ? "flex-col" : "flex-wrap"} gap-3`}>
        <button data-testid={compact ? "agency-sidebar-contact" : "agency-header-contact"} onClick={onContact} className="cta-gold min-h-12 px-6 text-sm font-semibold flex items-center justify-center gap-2">
            <MessageSquare size={17} /> Contatta
        </button>
        {agency.phone && (
            <a data-testid={compact ? "agency-sidebar-phone" : "agency-header-phone"} href={`tel:${agency.phone}`} className="cta-outline min-h-12 px-6 text-sm font-semibold flex items-center justify-center gap-2">
                <Phone size={17} /> Telefono
            </a>
        )}
    </div>
);

const TeamSection = ({ team }) => {
    if (!team?.length) return null;
    return (
        <section data-testid="agency-team-section" className="pt-4 pb-16">
            <span className="label-eyebrow">Le persone</span>
            <h2 data-testid="agency-team-title" className="font-serif text-3xl sm:text-4xl text-ink-50 mt-3 mb-8">Scopri il nostro team</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {team.map((member, index) => (
                    <article data-testid={`agency-team-member-${index}`} key={member.id || `${member.first_name}-${index}`} className="border border-white/10 bg-ink-900/45 p-6 text-center transition-transform duration-200 hover:-translate-y-1">
                        <div data-testid={`agency-team-photo-${index}`} className="w-28 h-28 rounded-full overflow-hidden mx-auto border-2 border-gold/30 bg-ink-950 grid place-items-center">
                            {member.photo_url ? <img src={mediaUrl(member.photo_url)} alt={`${member.first_name || "Membro"} ${member.last_name || ""}`.trim()} className="w-full h-full object-cover" loading="lazy" /> : <UserRound size={36} className="text-ink-500" />}
                        </div>
                        <h3 data-testid={`agency-team-name-${index}`} className="font-serif text-xl text-ink-50 mt-5">{`${member.first_name || ""} ${member.last_name || ""}`.trim() || "Membro del team"}</h3>
                        {member.role && <p data-testid={`agency-team-role-${index}`} className="text-sm text-ink-400 mt-1">{member.role}</p>}
                        {member.email && (
                            <a data-testid={`agency-team-email-${index}`} href={`mailto:${member.email}`} aria-label={`Scrivi a ${member.first_name} ${member.last_name}`} className="mt-4 inline-grid place-items-center w-10 h-10 rounded-full border border-white/15 text-gold hover:bg-gold hover:text-black transition-colors">
                                <Mail size={16} />
                            </a>
                        )}
                    </article>
                ))}
            </div>
        </section>
    );
};

export default function AgencyProfile() {
    const { id, slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showContact, setShowContact] = useState(false);

    useEffect(() => {
        const url = slug ? `/agencies/by-slug/${slug}` : `/agencies/${id}`;
        api.get(url).then((response) => setData(response.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, [id, slug]);

    const ag = data?.agency;
    useSeo({
        title: ag ? `${ag.name} - Agenzia immobiliare | VendiDirect` : "Agenzia immobiliare | VendiDirect",
        description: ag ? (ag.description || `Scopri gli immobili in vendita e in affitto proposti da ${ag.name} su VendiDirect.`) : undefined,
        canonical: ag?.slug ? `/agenzia/${ag.slug}` : id ? `/agenzie/${id}` : undefined,
        image: ag?.logo || ag?.banner,
        type: "profile",
        noindex: !loading && !data,
        jsonLd: ag ? {
            "@type": "RealEstateAgent", name: ag.name, description: ag.description || undefined,
            image: ag.logo || undefined, telephone: ag.phone || undefined,
            address: ag.address || undefined,
            url: publicUrl(ag.slug ? `/agenzia/${ag.slug}` : `/agenzie/${ag.user_id}`),
            sameAs: [ag.website, ...Object.values(ag.social || {})].filter(Boolean),
        } : null,
    }, [data, loading]);

    if (loading) return <div data-testid="agency-profile-loading" className="pt-32 text-center text-ink-400">Caricamento...</div>;
    if (!data) return <div data-testid="agency-profile-not-found" className="pt-32 text-center text-ink-400">Agenzia non trovata.</div>;

    const agency = data.agency;
    const year = agency.created_at ? new Date(agency.created_at).getFullYear() : null;
    return (
        <div data-testid="agency-profile-page" className="pt-28 pb-24">
            <header data-testid="agency-profile-header" className="py-6 lg:py-8">
                <div data-testid="agency-header-container" className={pageContainerClass}>
                    <div className="w-full min-w-0 border border-white/10 bg-ink-900/35 p-6 lg:p-8 flex flex-col md:flex-row items-start gap-7 lg:gap-10">
                        <div data-testid="agency-logo-wrap" className="w-28 h-28 sm:w-36 sm:h-36 shrink-0 bg-white border border-white/10 p-3 grid place-items-center">
                            {agency.logo ? <img data-testid="agency-logo" src={mediaUrl(agency.logo)} alt={`Logo ${agency.name}`} className="w-full h-full object-contain" /> : <Building2 size={44} className="text-ink-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="label-eyebrow">Agenzia su VendiDirect</span>
                            <h1 data-testid="agency-name" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink-50 mt-3 leading-tight break-words">{agency.name}</h1>
                            <div className="mt-5 flex flex-col gap-2 text-sm text-ink-300">
                                {agency.address && <div data-testid="agency-address" className="flex items-start gap-2"><MapPin size={16} className="text-gold mt-0.5 shrink-0" /><span>{agency.address}</span></div>}
                                {agency.website && <a data-testid="agency-website" href={agency.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-gold break-all"><Globe size={16} className="text-gold shrink-0" />Sito web <ExternalLink size={13} /></a>}
                            </div>
                            <div className="mt-7"><ActionButtons agency={agency} onContact={() => setShowContact(true)} /></div>
                            <SocialLinks social={agency.social} />
                        </div>
                    </div>
                </div>
            </header>

            <main data-testid="agency-main-container" className={`${pageContainerClass} py-16`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-16 items-start">
                    <div className="lg:col-span-8 min-w-0">
                        {agency.description && <p data-testid="agency-description" className="font-serif text-xl text-ink-100 leading-relaxed max-w-3xl mb-14">{agency.description}</p>}
                        <TeamSection team={agency.team} />
                        <section data-testid="agency-listings-section" className="pt-4">
                            <span className="label-eyebrow">Catalogo</span>
                            <div className="flex items-end justify-between gap-4 mt-3 mb-10">
                                <h2 data-testid="agency-listings-title" className="font-serif text-3xl sm:text-4xl text-ink-50">Immobili pubblicati</h2>
                                <span data-testid="agency-listings-count" className="text-xs uppercase tracking-wider text-ink-400">{data.count} annunci</span>
                            </div>
                            {data.listings.length === 0 ? (
                                <div data-testid="agency-listings-empty" className="border border-white/10 p-16 text-center text-ink-400">Nessun immobile pubblicato.</div>
                            ) : (
                                <div data-testid="agency-listings-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                    {data.listings.map((property, index) => <PropertyCard key={property.id} property={property} index={index} />)}
                                </div>
                            )}
                        </section>
                    </div>

                    <aside data-testid="agency-contact-sidebar" className="lg:col-span-4 lg:sticky lg:top-28 border border-white/10 bg-ink-900/55 p-7">
                        <div className="w-12 h-12 border border-gold/30 grid place-items-center text-gold mb-5"><Building2 size={21} /></div>
                        <h2 data-testid="agency-sidebar-name" className="font-serif text-2xl text-ink-50 break-words">{agency.name}</h2>
                        {agency.address && <p data-testid="agency-sidebar-address" className="text-sm text-ink-400 mt-3 flex items-start gap-2"><MapPin size={14} className="text-gold mt-0.5 shrink-0" />{agency.address}</p>}
                        <div className="mt-6"><ActionButtons agency={agency} compact onContact={() => setShowContact(true)} /></div>
                        <div className="mt-6 pt-5 border-t border-white/10 flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-500">
                            {year && <span data-testid="agency-member-since" className="flex items-center gap-1.5"><Calendar size={13} /> Dal {year}</span>}
                            <span data-testid="agency-sidebar-listings-count" className="flex items-center gap-1.5"><Building2 size={13} /> {data.count} immobili</span>
                        </div>
                    </aside>
                </div>
            </main>

            {showContact && (
                <div data-testid="agency-contact-modal" className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowContact(false)}>
                    <div className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto bg-ink-950 border border-gold/30" onClick={(event) => event.stopPropagation()}>
                        <button data-testid="agency-contact-modal-close" onClick={() => setShowContact(false)} aria-label="Chiudi contatto" className="absolute z-10 top-3 right-3 w-9 h-9 grid place-items-center border border-white/15 bg-ink-950 text-ink-300 hover:text-gold"><X size={17} /></button>
                        <ContactForm agencyId={agency.user_id} title={`Contatta ${agency.name}`} defaultMessage={`Salve, vorrei ricevere maggiori informazioni sui servizi e sugli immobili di ${agency.name}.`} />
                    </div>
                </div>
            )}
        </div>
    );
}